import { Fragment } from "react";
import type { CSSProperties } from "react";
import { SectionHeader } from "@/components/ui/";
import { cn } from "@/lib/utils";

type WallTile =
  | {
      kind: "logo";
      name: string;
      src: string;
      /** Single-tone artwork: `dark` marks are painted white on the dark theme,
          `light` marks (drawn for dark backgrounds) are painted black on light. */
      art?: "dark" | "light";
      /** Artwork that sits small inside a padded viewBox — give it a bigger box. */
      padded?: boolean;
    }
  | { kind: "more"; name: string; count: string };

interface WallColumn {
  id: string;
  tiles: WallTile[];
  /** Seconds for one full cycle — each column drifts at its own pace. */
  duration: number;
  /** Negative seconds; starts the loop mid-cycle so columns sit at different heights. */
  delay: number;
  reverse?: boolean;
}

/** Four columns, each with its own speed, direction, and starting offset so the
    wall never reads as one block sliding in unison. */
const COLUMNS: WallColumn[] = [
  {
    id: "col-1",
    duration: 68,
    delay: -8,
    tiles: [
      { kind: "logo", name: "Python", src: "/python-icon.svg" },
      { kind: "logo", name: "Playwright", src: "/playwright-icon.svg" },
      { kind: "logo", name: "n8n", src: "/n8n-icon.svg" },
      { kind: "logo", name: "Java", src: "/java-icon.svg" },
    ],
  },
  {
    id: "col-2",
    duration: 82,
    delay: -34,
    reverse: true,
    tiles: [
      { kind: "logo", name: "Node.js", src: "/nodejs-icon.svg" },
      { kind: "logo", name: "Scrapy", src: "/scrapy-icon.svg", art: "dark" },
      { kind: "logo", name: "Make", src: "/make-icon.svg" },
      { kind: "logo", name: "Ruby", src: "/ruby-icon.svg" },
    ],
  },
  {
    id: "col-3",
    duration: 74,
    delay: -19,
    tiles: [
      { kind: "logo", name: "Go", src: "/go-icon.svg" },
      { kind: "logo", name: "Puppeteer", src: "/puppeteer-icon.svg", art: "light" },
      { kind: "logo", name: "Zapier", src: "/zapier-icon.svg", art: "dark", padded: true },
      { kind: "logo", name: "C#", src: "/csharp-icon.svg" },
    ],
  },
  {
    id: "col-4",
    duration: 92,
    delay: -47,
    reverse: true,
    tiles: [
      { kind: "logo", name: "PHP", src: "/php-icon.svg" },
      { kind: "logo", name: "Selenium", src: "/selenium-icon.svg" },
      { kind: "logo", name: "cURL", src: "/curl-icon.svg", art: "dark" },
      { kind: "more", name: "More integrations", count: "+30" },
    ],
  },
];

const CATEGORIES = ["Languages", "Browser Automation", "Scraping Frameworks", "No-code / Workflow"];

/** Three copies of the list per track — see `.logo-wall-track` in globals.css. */
const COPIES = ["a", "b", "c"] as const;

// Soft top/bottom fade so tiles dissolve into the band instead of clipping.
const FADE_MASK = "linear-gradient(to bottom, transparent, #000 14%, #000 86%, transparent)";

interface TileProps {
  tile: WallTile;
  /** Duplicate copies exist only to make the loop seamless — hide them from AT. */
  duplicate: boolean;
}

function Tile({ tile, duplicate }: TileProps) {
  const shell =
    "border-border bg-card flex aspect-square flex-col items-center justify-center gap-2 rounded-xl border p-2 transition-[border-color] duration-300 sm:p-4";

  if (tile.kind === "more") {
    return (
      <div className={cn(shell, "border-dashed")} aria-hidden={duplicate || undefined}>
        <span className="text-foreground text-lg font-semibold sm:text-2xl">{tile.count}</span>
        <span className="text-muted-foreground hidden text-center text-xs leading-tight font-medium sm:block">
          More
        </span>
      </div>
    );
  }

  return (
    <div className={cn(shell, "hover:border-foreground/20")} aria-hidden={duplicate || undefined}>
      {/* Plain <img>: these marks are SVGs, which next/image refuses to serve
          through the optimizer unless `dangerouslyAllowSVG` is enabled. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={tile.src}
        alt={duplicate ? "" : tile.name}
        width={72}
        height={36}
        loading="lazy"
        decoding="async"
        className={cn(
          "w-auto object-contain",
          tile.padded ? "h-10 max-w-20 sm:h-16 sm:max-w-32" : "h-7 max-w-16 sm:h-10 sm:max-w-24",
          tile.art === "dark" && "light:brightness-100 light:invert-0 brightness-0 invert",
          tile.art === "light" && "light:brightness-0"
        )}
      />
      <span
        className="text-muted-foreground hidden text-center text-xs leading-tight font-medium sm:block"
        aria-hidden="true"
      >
        {tile.name}
      </span>
    </div>
  );
}

interface ColumnProps {
  column: WallColumn;
}

function Column({ column }: ColumnProps) {
  return (
    <div className="overflow-hidden">
      <div
        className={cn("logo-wall-track flex flex-col", column.reverse && "is-reverse")}
        style={
          {
            "--logo-wall-dur": `${column.duration}s`,
            "--logo-wall-delay": `${column.delay}s`,
          } as CSSProperties
        }
      >
        {COPIES.map((copy) => (
          <Fragment key={copy}>
            {column.tiles.map((tile) => (
              // Margin rather than gap: it makes each copy exactly one loop
              // step tall, including the trailing space, so the seam is invisible.
              <div key={`${copy}-${tile.name}`} className="mb-3 sm:mb-4">
                <Tile tile={tile} duplicate={copy !== "a"} />
              </div>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export default function IntegrationsSection() {
  return (
    <section
      id="integrations"
      className="full-bleed border-border bg-surface-1 py-section border-y"
      aria-labelledby="integrations-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="Integrations"
              id="integrations-heading"
              heading="Works with your entire stack."
              body="Drop Proxidize into your existing tools in minutes. Python, Node.js, Go, Playwright, Puppeteer, Selenium, n8n, Make, Zapier, and 30+ more."
            />

            <ul className="mt-8 flex flex-wrap gap-2" aria-label="Integration categories">
              {CATEGORIES.map((category) => (
                <li
                  key={category}
                  className="border-border text-muted-foreground rounded-full border px-3 py-1 text-xs font-medium"
                >
                  {category}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div
              className="logo-wall relative h-80 sm:h-96 lg:h-130"
              style={{ maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }}
              aria-label="Supported integrations"
            >
              <div className="grid h-full grid-cols-4 gap-3 sm:gap-4">
                {COLUMNS.map((column) => (
                  <Column key={column.id} column={column} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
